<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { RouterLink } from 'vue-router';
import {
  getBrowseLabel,
  getListingLabel,
  getListingLabelPlural,
  getListingLabelSingular,
  getMajorCategoryOverviewEnabled,
  hasCustomListingLabels,
  isOrderCreationAllowed,
} from '@/api/config';
import { useBrowseListingsStore } from '@/stores/browseListings';
import type {
  CategoryGroupWithAds,
  ClassificationWithAds,
  ClassificationWithCounts,
  SubgroupWithAds,
} from '@/stores/browseListings';
import type { ScheduledAdInfo, ScheduledRetailClassDisplayAdInfo } from '@/types/classifiedOrder';
import Button from '@/components/ui/Button.vue';
import ListingCard from '@/components/browse/ListingCard.vue';
import DisplayListingCard from '@/components/browse/DisplayListingCard.vue';

type MixedListingItem =
  | { type: 'regular'; listing: ScheduledAdInfo }
  | { type: 'display'; listing: ScheduledRetailClassDisplayAdInfo };

interface OverviewDescriptor {
  classification: ClassificationWithCounts;
  label: string;
}

type OverviewTarget = CategoryGroupWithAds | SubgroupWithAds;

type OverviewScope =
  | { kind: 'major'; classificationKey: number }
  | { kind: 'subgroup'; classificationKey: number; parentClassificationKey: number };

interface OverviewSection {
  descriptor: OverviewDescriptor;
  status: 'loading' | 'loaded' | 'error';
  regularListings: ScheduledAdInfo[];
  displayListings: ScheduledRetailClassDisplayAdInfo[];
  totalCount: number;
  nextSkip: number;
  hasMore: boolean;
}

interface OverviewLeafJob {
  index: number;
  generation: number;
  overviewToken: string;
  query: string;
}

interface LeafSearchState {
  query: string;
  regularListings: ScheduledAdInfo[];
  totalCount: number;
  nextSkip: number;
  hasMore: boolean;
}

interface CarriedSearch {
  query: string;
  regularListings: ScheduledAdInfo[];
  totalCount: number;
  nextSkip: number;
  hasMore: boolean;
}

const store = useBrowseListingsStore();
const majorOverviewEnabled = getMajorCategoryOverviewEnabled();
const allowOrderCreation = isOrderCreationAllowed();

const expandedCategories = ref<Set<number>>(new Set());
const expandedSubgroups = ref<Set<number>>(new Set());
const selectedOverviewScope = ref<OverviewScope | null>(null);
const selectedClassificationKey = ref<number | null>(null);
const selectedClassificationListings = ref<ClassificationWithAds | null>(null);

const searchInput = ref('');
const leafAppliedSearch = ref('');
const leafSearchState = ref<LeafSearchState | null>(null);
const leafSearchError = ref('');
const isSearching = ref(false);
const isLoadingMore = ref(false);
const leafLoadMoreError = ref('');
let searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;
let leafSearchGeneration = 0;
let leafLoadMoreGeneration = 0;
let viewGeneration = 0;

const majorSearchDraft = ref('');
const majorAppliedSearch = ref('');
const overviewSections = ref<OverviewSection[]>([]);
let overviewGeneration = 0;
let overviewActiveJobCount = 0;
let overviewJobQueue: OverviewLeafJob[] = [];
const stableDisplayListings = new Map<number, ScheduledRetailClassDisplayAdInfo[]>();
let prefetchStarted = false;
let prefetchTimer: ReturnType<typeof setTimeout> | null = null;

const categoryGroups = computed(() => store.categoryGroupsWithAds);
const isLoading = computed(() => store.loading);
const error = computed(() => store.error);
const hasAds = computed(() => store.hasAds);
const totalAdCount = computed(() => store.totalAdCount);
const browseLabel = computed(() => getBrowseLabel());
const showBrowseLabel = computed(() => browseLabel.value.length > 0);
const hasListingOverrides = computed(() => hasCustomListingLabels());
const listingLabelSingular = computed(() => getListingLabelSingular());
const listingLabelPlural = computed(() => getListingLabelPlural());

const selectedMajor = computed(() => {
  const scope = selectedOverviewScope.value;
  if (scope?.kind !== 'major') return null;
  return categoryGroups.value.find(group => group.classificationKey === scope.classificationKey) ?? null;
});

const selectedSubgroup = computed(() => {
  const scope = selectedOverviewScope.value;
  if (scope?.kind !== 'subgroup') return null;
  const parent = categoryGroups.value.find(group => group.classificationKey === scope.parentClassificationKey);
  return parent?.subgroups.find(subgroup => subgroup.classificationKey === scope.classificationKey) ?? null;
});

const selectedOverview = computed<OverviewTarget | null>(() => selectedSubgroup.value ?? selectedMajor.value);
const selectedMajorKey = computed(() => selectedOverviewScope.value?.kind === 'major'
  ? selectedOverviewScope.value.classificationKey
  : null);
const selectedSubgroupKey = computed(() => selectedOverviewScope.value?.kind === 'subgroup'
  ? selectedOverviewScope.value.classificationKey
  : null);
const selectedOverviewToken = computed(() => {
  const scope = selectedOverviewScope.value;
  return scope ? `${scope.kind}:${scope.classificationKey}` : '';
});

const selectedClassificationInfo = computed(() => {
  if (selectedClassificationKey.value === null) return null;
  for (const group of categoryGroups.value) {
    const direct = group.classifications.find(item => item.classificationKey === selectedClassificationKey.value);
    if (direct) return direct;
    for (const subgroup of group.subgroups) {
      const nested = subgroup.classifications.find(item => item.classificationKey === selectedClassificationKey.value);
      if (nested) return nested;
    }
  }
  return null;
});

const isLoadingClassification = computed(() =>
  !leafSearchState.value
  && store.loadingClassification === selectedClassificationKey.value,
);

function listingCountText(count: number): string {
  return hasListingOverrides.value
    ? `${count} ${getListingLabel(count)}`
    : `${count} ad${count !== 1 ? 's' : ''}`;
}

function showingListingCountText(loadedCount: number, totalCount: number): string {
  return hasListingOverrides.value
    ? `Showing ${loadedCount} of ${totalCount} ${getListingLabel(totalCount)}`
    : `Showing ${loadedCount} of ${totalCount} ad${totalCount !== 1 ? 's' : ''}`;
}

function overviewListingLabel(count: number): string {
  return hasListingOverrides.value ? getListingLabel(count) : `listing${count === 1 ? '' : 's'}`;
}

function categoryLabel(count: number): string {
  return `categor${count === 1 ? 'y' : 'ies'}`;
}

const browseSubtitle = computed(() => hasListingOverrides.value
  ? `Browse ${totalAdCount.value} ${getListingLabel(totalAdCount.value)} across ${categoryGroups.value.length} categories`
  : `Browse ${totalAdCount.value} ad${totalAdCount.value === 1 ? '' : 's'} across ${categoryGroups.value.length} categories`);
const placeListingText = computed(() => hasListingOverrides.value ? `Place Your Own ${listingLabelSingular.value}` : 'Place Your Own Ad');
const loadingListingsText = computed(() => hasListingOverrides.value ? `Loading ${listingLabelPlural.value}...` : 'Loading ads...');
const emptyListingsTitle = computed(() => hasListingOverrides.value ? `No ${listingLabelPlural.value} Currently Listed` : 'No Ads Currently Listed');
const emptyListingsText = computed(() => hasListingOverrides.value ? `Be the first to place your ${listingLabelSingular.value}.` : 'Be the first to place an ad!');
const selectClassificationText = computed(() => hasListingOverrides.value ? `Select a classification from the left to view ${listingLabelPlural.value}` : 'Select a classification from the left to view ads');
const loadingClassificationText = computed(() => hasListingOverrides.value
  ? `Loading ${listingLabelPlural.value} for ${selectedClassificationInfo.value?.name || 'classification'}...`
  : `Loading ads for ${selectedClassificationInfo.value?.name || 'classification'}...`);
const searchPlaceholder = computed(() => hasListingOverrides.value ? `Search ${listingLabelPlural.value}...` : 'Search ads...');

function shuffleArray<T>(items: T[]): T[] {
  const shuffled = [...items];
  for (let index = shuffled.length - 1; index > 0; index--) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [shuffled[index], shuffled[swapIndex]] = [shuffled[swapIndex], shuffled[index]];
  }
  return shuffled;
}

function stableShuffledDisplay(
  classificationKey: number,
  listings: ScheduledRetailClassDisplayAdInfo[],
): ScheduledRetailClassDisplayAdInfo[] {
  const existing = stableDisplayListings.get(classificationKey);
  const incomingKeys = new Set(listings.map(item => `${item.orderNumber}|${item.adImageURL}`));
  if (existing?.length === listings.length
    && existing.every(item => incomingKeys.has(`${item.orderNumber}|${item.adImageURL}`))) {
    return existing;
  }

  const shuffled = shuffleArray(listings);
  stableDisplayListings.set(classificationKey, shuffled);
  return shuffled;
}

function buildOverviewDescriptors(target: OverviewTarget): OverviewDescriptor[] {
  const ordered: ClassificationWithCounts[] = [];
  const seen = new Set<number>();
  for (const classification of target.classifications) {
    if (!seen.has(classification.classificationKey)) {
      seen.add(classification.classificationKey);
      ordered.push(classification);
    }
  }
  if ('subgroups' in target) {
    for (const subgroup of target.subgroups) {
      for (const classification of subgroup.classifications) {
        if (!seen.has(classification.classificationKey)) {
          seen.add(classification.classificationKey);
          ordered.push(classification);
        }
      }
    }
  }

  const duplicateCounts = new Map<string, number>();
  for (const classification of ordered) {
    const normalizedName = classification.name.trim().toLocaleLowerCase();
    duplicateCounts.set(normalizedName, (duplicateCounts.get(normalizedName) ?? 0) + 1);
  }

  return ordered.map(classification => {
    const normalizedName = classification.name.trim().toLocaleLowerCase();
    if ((duplicateCounts.get(normalizedName) ?? 0) < 2) {
      return { classification, label: classification.name };
    }

    const path: string[] = [];
    let parent = store.classificationMap.get(classification.classificationKey)?.parent ?? null;
    while (parent && parent.classificationKey !== target.classificationKey) {
      path.unshift(parent.name);
      parent = store.classificationMap.get(parent.classificationKey)?.parent ?? null;
    }

    return {
      classification,
      label: path.length > 0 ? `${path.join(' / ')} / ${classification.name}` : classification.name,
    };
  });
}

const selectedOverviewDescriptors = computed(() =>
  selectedOverview.value ? buildOverviewDescriptors(selectedOverview.value) : [],
);

function updateOverviewSection(index: number, section: OverviewSection) {
  const next = [...overviewSections.value];
  next[index] = section;
  overviewSections.value = next;
}

function makeLoadedOverviewSection(
  descriptor: OverviewDescriptor,
  regularListings: ScheduledAdInfo[],
  displayListings: ScheduledRetailClassDisplayAdInfo[],
  totalCount: number,
  hasMore: boolean,
  nextSkip = 0,
): OverviewSection {
  return {
    descriptor,
    status: 'loaded',
    regularListings,
    displayListings: displayListings.length > 0
      ? stableShuffledDisplay(descriptor.classification.classificationKey, displayListings)
      : [],
    totalCount,
    nextSkip,
    hasMore,
  };
}

function seedUnfilteredOverview(descriptors: OverviewDescriptor[]): number[] {
  const pendingIndices: number[] = [];
  overviewSections.value = descriptors.map((descriptor, index) => {
    const key = descriptor.classification.classificationKey;
    const cached = store.getAdsForClassification(key);
    if (!cached) {
      pendingIndices.push(index);
      return {
        descriptor,
        status: 'loading',
        regularListings: [],
        displayListings: [],
        totalCount: descriptor.classification.totalAdCount,
        nextSkip: 0,
        hasMore: false,
      };
    }

    void store.ensureCustomFieldsForClassification(key);
    const pagination = store.paginationState.get(key);
    return makeLoadedOverviewSection(
      descriptor,
      cached.regularAds,
      cached.displayAds,
      descriptor.classification.totalAdCount,
      pagination?.hasMore ?? false,
    );
  });
  return pendingIndices;
}

function isCurrentOverviewRequest(generation: number, overviewToken: string, query: string): boolean {
  return generation === overviewGeneration
    && selectedOverviewToken.value === overviewToken
    && majorAppliedSearch.value === query;
}

async function executeOverviewJob(job: OverviewLeafJob) {
  if (!isCurrentOverviewRequest(job.generation, job.overviewToken, job.query)) return;
  const current = overviewSections.value[job.index];
  if (!current) return;
  const descriptor = current.descriptor;
  const key = descriptor.classification.classificationKey;

  try {
    if (job.query) {
      const result = await store.fetchClassificationSearchPage(key, job.query);
      if (!isCurrentOverviewRequest(job.generation, job.overviewToken, job.query)) return;
      updateOverviewSection(job.index, makeLoadedOverviewSection(
        descriptor,
        result.regularAds,
        [],
        result.totalCount,
        result.hasMore,
        result.nextSkip,
      ));
    } else {
      const listings = await store.fetchAdsForClassification(key, false);
      if (!isCurrentOverviewRequest(job.generation, job.overviewToken, job.query)) return;
      if (!listings) throw new Error('The category could not be loaded.');
      const pagination = store.paginationState.get(key);
      updateOverviewSection(job.index, makeLoadedOverviewSection(
        descriptor,
        listings.regularAds,
        listings.displayAds,
        descriptor.classification.totalAdCount,
        pagination?.hasMore ?? false,
      ));
    }
  } catch {
    if (!isCurrentOverviewRequest(job.generation, job.overviewToken, job.query)) return;
    updateOverviewSection(job.index, {
      ...current,
      status: 'error',
      regularListings: [],
      displayListings: [],
    });
  }
}

function pumpOverviewJobs() {
  overviewJobQueue = overviewJobQueue.filter(job =>
    isCurrentOverviewRequest(job.generation, job.overviewToken, job.query),
  );

  while (overviewActiveJobCount < 3 && overviewJobQueue.length > 0) {
    const job = overviewJobQueue.shift()!;
    overviewActiveJobCount += 1;
    void executeOverviewJob(job).finally(() => {
      overviewActiveJobCount -= 1;
      pumpOverviewJobs();
    });
  }
}

function queueOverviewSections(
  indices: number[],
  generation: number,
  overviewToken: string,
  query: string,
) {
  // Running requests retain their slots until they settle. Only queued work can
  // be dropped, which keeps the component-wide request ceiling at three.
  overviewJobQueue = overviewJobQueue.filter(job =>
    isCurrentOverviewRequest(job.generation, job.overviewToken, job.query),
  );
  overviewJobQueue.push(...indices.map(index => ({ index, generation, overviewToken, query })));
  pumpOverviewJobs();
}

function startUnfilteredOverview(target: OverviewTarget) {
  const descriptors = buildOverviewDescriptors(target);
  const overviewToken = selectedOverviewToken.value;
  if (!overviewToken) return;
  majorAppliedSearch.value = '';
  const generation = ++overviewGeneration;
  const indices = seedUnfilteredOverview(descriptors);
  queueOverviewSections(indices, generation, overviewToken, '');
}

const overviewLoadedCount = computed(() => overviewSections.value.reduce(
  (total, section) => total + section.regularListings.length + section.displayListings.length,
  0,
));
const overviewVisibleCategoryCount = computed(() => overviewSections.value.filter(
  section => section.status === 'loaded'
    && section.regularListings.length + section.displayListings.length > 0,
).length);
const overviewFailureSections = computed(() => overviewSections.value.filter(section => section.status === 'error'));
const overviewHasFailures = computed(() => overviewFailureSections.value.length > 0);
const overviewAllSettled = computed(() => overviewSections.value.every(section => section.status !== 'loading'));
const overviewHasVisibleListings = computed(() => overviewLoadedCount.value > 0);
const overviewShowFullError = computed(() =>
  overviewAllSettled.value && overviewHasFailures.value && !overviewHasVisibleListings.value,
);
const overviewShowEmpty = computed(() =>
  overviewAllSettled.value
    && !overviewHasFailures.value
    && !overviewHasVisibleListings.value
    && overviewSections.value.every(section => !section.hasMore),
);
const failedOverviewNames = computed(() => overviewFailureSections.value.map(section => section.descriptor.label).join(', '));

const overviewHeading = computed(() => {
  const visibleCategories = overviewVisibleCategoryCount.value;
  if (majorAppliedSearch.value) {
    if (!overviewAllSettled.value || overviewHasFailures.value) {
      return `Showing ${overviewLoadedCount.value} ${overviewLoadedCount.value === 1 ? 'match' : 'matches'} across ${visibleCategories} ${categoryLabel(visibleCategories)}`;
    }

    const totalMatches = overviewSections.value.reduce((total, section) => total + section.totalCount, 0);
    if (overviewLoadedCount.value < totalMatches) {
      return `Showing ${overviewLoadedCount.value} of ${totalMatches} matches across ${visibleCategories} ${categoryLabel(visibleCategories)}`;
    }
    return `${totalMatches} ${totalMatches === 1 ? 'match' : 'matches'} across ${visibleCategories} ${categoryLabel(visibleCategories)}`;
  }

  const expectedListings = selectedOverview.value?.totalAdCount ?? 0;
  const expectedCategories = selectedOverviewDescriptors.value.length;
  const complete = overviewAllSettled.value
    && !overviewHasFailures.value
    && overviewSections.value.every(section => !section.hasMore)
    && overviewLoadedCount.value === expectedListings
    && visibleCategories === expectedCategories;
  if (complete) {
    return `${expectedListings} ${overviewListingLabel(expectedListings)} across ${visibleCategories} ${categoryLabel(visibleCategories)}`;
  }
  return `Showing ${overviewLoadedCount.value} of ${expectedListings} ${overviewListingLabel(expectedListings)} across ${visibleCategories} of ${expectedCategories} ${categoryLabel(expectedCategories)}`;
});

function retryOverviewFailures() {
  if (!overviewAllSettled.value
    || !selectedOverview.value
    || !selectedOverviewToken.value
    || overviewFailureSections.value.length === 0) return;
  const failedKeys = new Set(overviewFailureSections.value.map(section => section.descriptor.classification.classificationKey));
  const indices: number[] = [];
  overviewSections.value = overviewSections.value.map((section, index) => {
    if (!failedKeys.has(section.descriptor.classification.classificationKey)) return section;
    indices.push(index);
    return { ...section, status: 'loading' };
  });
  const generation = ++overviewGeneration;
  queueOverviewSections(indices, generation, selectedOverviewToken.value, majorAppliedSearch.value);
}

function submitMajorSearch() {
  if (!selectedOverview.value || !selectedOverviewToken.value) return;
  const query = majorSearchDraft.value.trim();
  if (!query) return;

  stopBackgroundPrefetchForOverview();
  majorSearchDraft.value = query;
  majorAppliedSearch.value = query;
  const descriptors = buildOverviewDescriptors(selectedOverview.value)
    .filter(descriptor => descriptor.classification.regularAdCount > 0);
  overviewSections.value = descriptors.map(descriptor => ({
    descriptor,
    status: 'loading',
    regularListings: [],
    displayListings: [],
    totalCount: 0,
    nextSkip: 0,
    hasMore: false,
  }));
  const generation = ++overviewGeneration;
  queueOverviewSections(
    descriptors.map((_, index) => index),
    generation,
    selectedOverviewToken.value,
    query,
  );
}

function clearMajorSearch() {
  if (!selectedOverview.value) return;
  majorSearchDraft.value = '';
  majorAppliedSearch.value = '';
  startUnfilteredOverview(selectedOverview.value);
}

function clearLeafSearchState(clearInput = true) {
  if (searchDebounceTimer) {
    clearTimeout(searchDebounceTimer);
    searchDebounceTimer = null;
  }
  leafSearchGeneration += 1;
  if (clearInput) searchInput.value = '';
  leafAppliedSearch.value = '';
  leafSearchState.value = null;
  leafSearchError.value = '';
  isSearching.value = false;
  store.currentSearchText = '';
  leafLoadMoreGeneration += 1;
  isLoadingMore.value = false;
  leafLoadMoreError.value = '';
}

function restoreUnfilteredLeaf() {
  const classificationKey = selectedClassificationKey.value;
  if (classificationKey === null) return;
  const cached = store.getAdsForClassification(classificationKey);
  if (cached) {
    selectedClassificationListings.value = cached;
    return;
  }
  const generation = viewGeneration;
  void store.fetchAdsForClassification(classificationKey).then(listings => {
    if (listings
      && generation === viewGeneration
      && selectedClassificationKey.value === classificationKey) {
      selectedClassificationListings.value = listings;
    }
  });
}

async function executeLeafSearch(classificationKey: number, query: string, generation: number) {
  isSearching.value = true;
  leafSearchError.value = '';
  try {
    const result = await store.fetchClassificationSearchPage(classificationKey, query);
    if (generation !== leafSearchGeneration || selectedClassificationKey.value !== classificationKey) return;
    leafAppliedSearch.value = result.searchText;
    leafSearchState.value = {
      query: result.searchText,
      regularListings: result.regularAds,
      totalCount: result.totalCount,
      nextSkip: result.nextSkip,
      hasMore: result.hasMore,
    };
  } catch {
    if (generation === leafSearchGeneration && selectedClassificationKey.value === classificationKey) {
      leafSearchError.value = 'Search could not be completed. Please try again.';
    }
  } finally {
    if (generation === leafSearchGeneration) isSearching.value = false;
  }
}

async function executeLegacyLeafSearch(classificationKey: number, query: string, generation: number) {
  isSearching.value = true;
  try {
    const listings = await store.searchAdsLegacy(classificationKey, query);
    if (generation !== leafSearchGeneration || selectedClassificationKey.value !== classificationKey) return;
    if (listings) {
      selectedClassificationListings.value = listings;
    }
  } catch {
    // Legacy minor search historically leaves the current results in place on failure.
  } finally {
    if (generation === leafSearchGeneration) isSearching.value = false;
  }
}

function onSearchInput() {
  if (searchDebounceTimer) clearTimeout(searchDebounceTimer);
  const classificationKey = selectedClassificationKey.value;
  const generation = ++leafSearchGeneration;
  leafLoadMoreGeneration += 1;
  isLoadingMore.value = false;
  leafLoadMoreError.value = '';

  if (classificationKey === null) {
    isSearching.value = false;
    return;
  }

  if (!majorOverviewEnabled) {
    const query = searchInput.value;
    leafAppliedSearch.value = query.trim();
    leafSearchState.value = null;
    leafSearchError.value = '';
    isSearching.value = true;
    searchDebounceTimer = setTimeout(() => {
      void executeLegacyLeafSearch(classificationKey, query, generation);
    }, 300);
    return;
  }

  const query = searchInput.value.trim();
  if (!query) {
    leafAppliedSearch.value = '';
    leafSearchState.value = null;
    leafSearchError.value = '';
    isSearching.value = false;
    restoreUnfilteredLeaf();
    return;
  }

  isSearching.value = true;
  searchDebounceTimer = setTimeout(() => {
    void executeLeafSearch(classificationKey, query, generation);
  }, 300);
}

function clearSearch() {
  if (!majorOverviewEnabled) {
    searchInput.value = '';
    onSearchInput();
    return;
  }
  clearLeafSearchState(true);
  restoreUnfilteredLeaf();
}

const paginationInfo = computed(() => {
  if (leafSearchState.value) {
    return {
      totalCount: leafSearchState.value.totalCount,
      loadedCount: leafSearchState.value.regularListings.length,
      hasMore: leafSearchState.value.hasMore,
    };
  }
  if (selectedClassificationKey.value === null) return null;
  return store.paginationState.get(selectedClassificationKey.value) ?? null;
});

const combinedListings = computed<MixedListingItem[]>(() => {
  if (leafSearchState.value) {
    return leafSearchState.value.regularListings.map(listing => ({ type: 'regular' as const, listing }));
  }
  if (!selectedClassificationListings.value) return [];
  const regular = selectedClassificationListings.value.regularAds.map(listing => ({ type: 'regular' as const, listing }));
  const display = stableShuffledDisplay(
    selectedClassificationListings.value.classificationKey,
    selectedClassificationListings.value.displayAds,
  ).map(listing => ({ type: 'display' as const, listing }));
  return [...regular, ...display];
});

async function loadMore() {
  const classificationKey = selectedClassificationKey.value;
  if (classificationKey === null || isLoadingMore.value || isSearching.value) return;
  const operationGeneration = ++leafLoadMoreGeneration;
  const selectionGeneration = viewGeneration;
  isLoadingMore.value = true;
  leafLoadMoreError.value = '';
  try {
    if (leafSearchState.value) {
      const current = leafSearchState.value;
      const searchGeneration = leafSearchGeneration;
      const result = await store.fetchClassificationSearchPage(
        classificationKey,
        current.query,
        current.nextSkip,
      );
      if (operationGeneration !== leafLoadMoreGeneration
        || searchGeneration !== leafSearchGeneration
        || selectionGeneration !== viewGeneration
        || selectedClassificationKey.value !== classificationKey
        || leafSearchState.value?.query !== current.query) return;
      leafSearchState.value = {
        ...current,
        regularListings: [...current.regularListings, ...result.regularAds],
        totalCount: result.totalCount,
        nextSkip: result.nextSkip,
        hasMore: result.hasMore,
      };
      return;
    }

    const previousPagination = store.paginationState.get(classificationKey);
    await store.loadMoreAds(classificationKey);
    if (operationGeneration !== leafLoadMoreGeneration
      || selectionGeneration !== viewGeneration
      || selectedClassificationKey.value !== classificationKey) return;

    const nextPagination = store.paginationState.get(classificationKey);
    const cached = store.getAdsForClassification(classificationKey);
    if (cached) {
      selectedClassificationListings.value = cached;
    }
    if (previousPagination?.hasMore
      && (!cached || !nextPagination || nextPagination.loadedCount <= previousPagination.loadedCount)) {
      leafLoadMoreError.value = 'More listings could not be loaded. Please try again.';
    }
  } catch {
    if (operationGeneration === leafLoadMoreGeneration
      && selectionGeneration === viewGeneration
      && selectedClassificationKey.value === classificationKey) {
      leafLoadMoreError.value = leafSearchState.value
        ? 'More matches could not be loaded. Please try again.'
        : 'More listings could not be loaded. Please try again.';
    }
  } finally {
    if (operationGeneration === leafLoadMoreGeneration) {
      isLoadingMore.value = false;
    }
  }
}

function startBackgroundPrefetch(classificationKey: number) {
  if (prefetchStarted) return;
  prefetchStarted = true;
  prefetchTimer = setTimeout(() => {
    prefetchTimer = null;
    if (selectedClassificationKey.value === classificationKey) {
      store.startBackgroundPrefetch(classificationKey);
      return;
    }

    prefetchStarted = false;
    if (selectedClassificationKey.value !== null) {
      startBackgroundPrefetch(selectedClassificationKey.value);
    }
  }, 1000);
}

function stopBackgroundPrefetchForOverview() {
  if (prefetchTimer) {
    clearTimeout(prefetchTimer);
    prefetchTimer = null;
  }
  prefetchStarted = false;
  store.stopBackgroundPrefetch();
}

async function selectClassification(
  classification: ClassificationWithCounts,
  carriedSearch?: CarriedSearch,
) {
  const generation = ++viewGeneration;
  overviewGeneration += 1;
  overviewJobQueue = [];
  selectedOverviewScope.value = null;
  overviewSections.value = [];
  majorSearchDraft.value = '';
  majorAppliedSearch.value = '';
  clearLeafSearchState(true);
  selectedClassificationKey.value = classification.classificationKey;
  selectedClassificationListings.value = store.getAdsForClassification(classification.classificationKey);

  if (carriedSearch) {
    searchInput.value = carriedSearch.query;
    leafAppliedSearch.value = carriedSearch.query;
    leafSearchState.value = {
      query: carriedSearch.query,
      regularListings: [...carriedSearch.regularListings],
      totalCount: carriedSearch.totalCount,
      nextSkip: carriedSearch.nextSkip,
      hasMore: carriedSearch.hasMore,
    };
  }

  const listings = await store.fetchAdsForClassification(classification.classificationKey, !carriedSearch);
  if (generation !== viewGeneration || selectedClassificationKey.value !== classification.classificationKey) return;
  if (listings) {
    selectedClassificationListings.value = listings;
    startBackgroundPrefetch(classification.classificationKey);
  }
}

function expandSubgroupForLeaf(group: CategoryGroupWithAds, classificationKey: number) {
  const subgroup = group.subgroups.find(item =>
    item.classifications.some(classification => classification.classificationKey === classificationKey),
  );
  if (!subgroup) return;
  expandedSubgroups.value.add(subgroup.classificationKey);
  expandedSubgroups.value = new Set(expandedSubgroups.value);
}

async function selectMajor(group: CategoryGroupWithAds) {
  const descriptors = buildOverviewDescriptors(group);
  expandedCategories.value.add(group.classificationKey);
  expandedCategories.value = new Set(expandedCategories.value);
  if (descriptors.length === 1) {
    expandSubgroupForLeaf(group, descriptors[0].classification.classificationKey);
    await selectClassification(descriptors[0].classification);
    return;
  }

  viewGeneration += 1;
  leafSearchGeneration += 1;
  clearLeafSearchState(true);
  selectedClassificationKey.value = null;
  selectedClassificationListings.value = null;
  selectedOverviewScope.value = {
    kind: 'major',
    classificationKey: group.classificationKey,
  };
  majorSearchDraft.value = '';
  stopBackgroundPrefetchForOverview();
  startUnfilteredOverview(group);
}

async function selectSubgroup(group: CategoryGroupWithAds, subgroup: SubgroupWithAds) {
  const descriptors = buildOverviewDescriptors(subgroup);
  expandedCategories.value.add(group.classificationKey);
  expandedCategories.value = new Set(expandedCategories.value);
  expandedSubgroups.value.add(subgroup.classificationKey);
  expandedSubgroups.value = new Set(expandedSubgroups.value);
  if (descriptors.length === 1) {
    await selectClassification(descriptors[0].classification);
    return;
  }

  viewGeneration += 1;
  leafSearchGeneration += 1;
  clearLeafSearchState(true);
  selectedClassificationKey.value = null;
  selectedClassificationListings.value = null;
  selectedOverviewScope.value = {
    kind: 'subgroup',
    classificationKey: subgroup.classificationKey,
    parentClassificationKey: group.classificationKey,
  };
  majorSearchDraft.value = '';
  stopBackgroundPrefetchForOverview();
  startUnfilteredOverview(subgroup);
}

function openOverviewContinuation(section: OverviewSection) {
  const classification = section.descriptor.classification;
  if (majorAppliedSearch.value) {
    void selectClassification(classification, {
      query: majorAppliedSearch.value,
      regularListings: section.regularListings,
      totalCount: section.totalCount,
      nextSkip: section.nextSkip,
      hasMore: section.hasMore,
    });
    return;
  }
  void selectClassification(classification);
}

function toggleCategory(classificationKey: number) {
  if (expandedCategories.value.has(classificationKey)) expandedCategories.value.delete(classificationKey);
  else expandedCategories.value.add(classificationKey);
  expandedCategories.value = new Set(expandedCategories.value);
}

function toggleSubgroup(classificationKey: number) {
  if (expandedSubgroups.value.has(classificationKey)) expandedSubgroups.value.delete(classificationKey);
  else expandedSubgroups.value.add(classificationKey);
  expandedSubgroups.value = new Set(expandedSubgroups.value);
}

watch(categoryGroups, async groups => {
  if (groups.length === 0 || expandedCategories.value.size > 0) return;
  const firstGroup = groups[0];
  expandedCategories.value.add(firstGroup.classificationKey);
  expandedCategories.value = new Set(expandedCategories.value);

  if (majorOverviewEnabled) {
    await selectMajor(firstGroup);
    return;
  }

  if (firstGroup.classifications.length > 0) {
    await selectClassification(firstGroup.classifications[0]);
  } else if (firstGroup.subgroups.length > 0) {
    const firstSubgroup = firstGroup.subgroups[0];
    expandedSubgroups.value.add(firstSubgroup.classificationKey);
    expandedSubgroups.value = new Set(expandedSubgroups.value);
    if (firstSubgroup.classifications.length > 0) {
      await selectClassification(firstSubgroup.classifications[0]);
    }
  }
}, { immediate: true });

// Sticky sidebar positioning for iframe embed mode.
const sidebarRef = ref<HTMLElement | null>(null);
const sidebarTranslateY = ref(0);
const stackedSidebarMediaQuery = window.matchMedia('(max-width: 768px)');

function getAbsoluteOffsetTop(element: HTMLElement): number {
  let top = 0;
  let current: HTMLElement | null = element;
  while (current) {
    top += current.offsetTop;
    current = current.offsetParent as HTMLElement;
  }
  return top;
}

function handleParentScroll(event: MessageEvent) {
  if (event.data?.type !== 'FB_IFRAME_SCROLL_OFFSET') return;
  const sidebar = sidebarRef.value;
  if (!sidebar) return;

  if (stackedSidebarMediaQuery.matches) {
    sidebarTranslateY.value = 0;
    return;
  }

  const parentScrollTop = Number(event.data.scrollTop) || 0;
  const visibleTop = event.data.visibleTop != null ? Number(event.data.visibleTop) : null;
  const effectiveScrollTop = visibleTop !== null ? visibleTop : parentScrollTop + 80;
  const sidebarTop = getAbsoluteOffsetTop(sidebar);
  const grid = sidebar.parentElement;
  if (!grid) return;
  const maxOffset = getAbsoluteOffsetTop(grid) + grid.offsetHeight - sidebarTop - sidebar.offsetHeight;
  const desiredOffset = effectiveScrollTop + 20 - sidebarTop;
  sidebarTranslateY.value = desiredOffset > 0 && maxOffset > 0 ? Math.min(desiredOffset, maxOffset) : 0;
}

function handleSidebarLayoutChange(event: MediaQueryListEvent) {
  if (event.matches) sidebarTranslateY.value = 0;
}

onMounted(() => {
  window.addEventListener('message', handleParentScroll);
  stackedSidebarMediaQuery.addEventListener('change', handleSidebarLayoutChange);
  void store.fetchCounts();
});

onUnmounted(() => {
  window.removeEventListener('message', handleParentScroll);
  stackedSidebarMediaQuery.removeEventListener('change', handleSidebarLayoutChange);
  if (searchDebounceTimer) clearTimeout(searchDebounceTimer);
  overviewGeneration += 1;
  overviewJobQueue = [];
  leafSearchGeneration += 1;
  leafLoadMoreGeneration += 1;
  viewGeneration += 1;
  stopBackgroundPrefetchForOverview();
});
</script>

<template>
  <div class="home-view">
    <!-- Header with CTA -->
    <header class="page-header">
      <div class="header-content">
        <div class="header-text">
          <h1 v-if="showBrowseLabel" class="page-title">{{ browseLabel }}</h1>
          <p v-if="hasAds" class="page-subtitle">
            {{ browseSubtitle }}
          </p>
        </div>
        <RouterLink v-if="allowOrderCreation" to="/create-ad" class="cta-link">
          <Button variant="primary" size="lg">
            {{ placeListingText }}
          </Button>
        </RouterLink>
      </div>
    </header>

    <!-- Loading State -->
    <div v-if="isLoading" class="loading-container">
      <div class="loading-spinner"></div>
      <p class="loading-text">{{ loadingListingsText }}</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-container">
      <div class="error-icon">!</div>
      <p class="error-text">{{ error }}</p>
      <Button variant="secondary" @click="store.fetchCounts()">
        Try Again
      </Button>
    </div>

    <!-- Empty State -->
    <div v-else-if="!hasAds" class="empty-container">
      <div class="empty-icon">
        <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
          <polyline points="14 2 14 8 20 8"></polyline>
          <line x1="12" y1="18" x2="12" y2="12"></line>
          <line x1="9" y1="15" x2="15" y2="15"></line>
        </svg>
      </div>
      <h2 class="empty-title">{{ emptyListingsTitle }}</h2>
      <p class="empty-text">{{ emptyListingsText }}</p>
      <RouterLink v-if="allowOrderCreation" to="/create-ad">
        <Button variant="primary" size="lg">
          {{ placeListingText }}
        </Button>
      </RouterLink>
    </div>

    <!-- Main Content with Sidebar -->
    <div v-else class="main-layout">
      <!-- Sidebar Navigation -->
      <aside
        ref="sidebarRef"
        class="sidebar"
        :style="sidebarTranslateY > 0 ? { transform: `translateY(${sidebarTranslateY}px)` } : undefined"
      >
        <nav class="sidebar-nav">
          <div class="sidebar-header">Categories</div>
          <ul class="category-list">
            <li 
              v-for="group in categoryGroups" 
              :key="group.classificationKey"
              class="category-item"
            >
              <button
                v-if="!majorOverviewEnabled"
                class="category-button"
                :class="{ expanded: expandedCategories.has(group.classificationKey) }"
                @click="toggleCategory(group.classificationKey)"
              >
                <span class="category-name">{{ group.name }}</span>
                <span class="category-badge">{{ group.totalAdCount }}</span>
                <svg
                  class="expand-icon"
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>

              <div v-else class="category-control">
                <button
                  class="category-select-button"
                  :class="{ active: selectedMajorKey === group.classificationKey }"
                  :aria-current="selectedMajorKey === group.classificationKey ? 'page' : undefined"
                  @click="selectMajor(group)"
                >
                  <span class="category-name">{{ group.name }}</span>
                  <span class="category-badge">{{ group.totalAdCount }}</span>
                </button>
                <button
                  class="category-toggle-button"
                  :class="{ expanded: expandedCategories.has(group.classificationKey) }"
                  :aria-label="`${expandedCategories.has(group.classificationKey) ? 'Collapse' : 'Expand'} ${group.name}`"
                  :aria-expanded="expandedCategories.has(group.classificationKey)"
                  :aria-controls="`category-panel-${group.classificationKey}`"
                  @click="toggleCategory(group.classificationKey)"
                >
                  <svg
                    class="expand-icon"
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </button>
              </div>
              
              <!-- Level 2: Direct leaf classifications and subgroups -->
              <ul 
                v-if="expandedCategories.has(group.classificationKey)" 
                :id="`category-panel-${group.classificationKey}`"
                class="subcategory-list"
              >
                <!-- Direct leaf classifications (level 2) -->
                <li 
                  v-for="classification in group.classifications" 
                  :key="classification.classificationKey"
                >
                  <button 
                    class="subcategory-button"
                    :class="{ active: selectedClassificationKey === classification.classificationKey }"
                    @click="selectClassification(classification)"
                  >
                    <span class="subcategory-name">{{ classification.name }}</span>
                    <span class="subcategory-badge">{{ classification.totalAdCount }}</span>
                  </button>
                </li>
                
                <!-- Subgroups (level 2 groups that contain level 3 leaves) -->
                <li 
                  v-for="subgroup in group.subgroups" 
                  :key="subgroup.classificationKey"
                  class="subgroup-item"
                >
                  <button 
                    v-if="!majorOverviewEnabled"
                    class="subgroup-button"
                    :class="{ expanded: expandedSubgroups.has(subgroup.classificationKey) }"
                    @click="toggleSubgroup(subgroup.classificationKey)"
                  >
                    <span class="subgroup-name">{{ subgroup.name }}</span>
                    <span class="subgroup-badge">{{ subgroup.totalAdCount }}</span>
                    <svg 
                      class="expand-icon" 
                      xmlns="http://www.w3.org/2000/svg" 
                      width="14" 
                      height="14" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      stroke-width="2" 
                      stroke-linecap="round" 
                      stroke-linejoin="round"
                    >
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </button>

                  <div v-else class="subgroup-control">
                    <button
                      class="subgroup-select-button"
                      :class="{ active: selectedSubgroupKey === subgroup.classificationKey }"
                      :aria-current="selectedSubgroupKey === subgroup.classificationKey ? 'page' : undefined"
                      @click="selectSubgroup(group, subgroup)"
                    >
                      <span class="subgroup-name">{{ subgroup.name }}</span>
                      <span class="subgroup-badge">{{ subgroup.totalAdCount }}</span>
                    </button>
                    <button
                      class="subgroup-toggle-button"
                      :class="{ expanded: expandedSubgroups.has(subgroup.classificationKey) }"
                      :aria-label="`${expandedSubgroups.has(subgroup.classificationKey) ? 'Collapse' : 'Expand'} ${subgroup.name}`"
                      :aria-expanded="expandedSubgroups.has(subgroup.classificationKey)"
                      :aria-controls="`subgroup-panel-${subgroup.classificationKey}`"
                      @click="toggleSubgroup(subgroup.classificationKey)"
                    >
                      <svg
                        class="expand-icon"
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        aria-hidden="true"
                      >
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    </button>
                  </div>
                  
                  <!-- Level 3: Leaf classifications within subgroup -->
                  <ul 
                    v-if="expandedSubgroups.has(subgroup.classificationKey)" 
                    :id="`subgroup-panel-${subgroup.classificationKey}`"
                    class="level3-list"
                  >
                    <li 
                      v-for="classification in subgroup.classifications" 
                      :key="classification.classificationKey"
                    >
                      <button 
                        class="level3-button"
                        :class="{ active: selectedClassificationKey === classification.classificationKey }"
                        @click="selectClassification(classification)"
                      >
                        <span class="level3-name">{{ classification.name }}</span>
                        <span class="level3-badge">{{ classification.totalAdCount }}</span>
                      </button>
                    </li>
                  </ul>
                </li>
              </ul>
            </li>
          </ul>
        </nav>
      </aside>

      <!-- Listings panel -->
      <section class="listing-panel">
        <div v-if="selectedOverview && selectedOverviewScope" class="major-overview">
          <div class="classification-header major-overview-header">
            <h2 class="classification-title">{{ selectedOverview.name }}</h2>
            <span class="classification-count">{{ overviewHeading }}</span>
          </div>

          <form class="overview-search-form" @submit.prevent="submitMajorSearch">
            <div class="search-input-wrapper">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="search-icon" aria-hidden="true">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
              <input
                v-model="majorSearchDraft"
                type="text"
                :placeholder="searchPlaceholder"
                class="search-input"
                aria-label="Search this category overview"
              />
              <button
                v-if="majorSearchDraft || majorAppliedSearch"
                type="button"
                class="search-clear-btn"
                title="Clear search"
                aria-label="Clear category overview search"
                @click="clearMajorSearch"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <button type="submit" class="overview-search-submit" :disabled="!majorSearchDraft.trim()">
              Search
            </button>
          </form>

          <div v-if="overviewHasFailures && overviewHasVisibleListings" class="overview-warning" role="status">
            <span>Could not load: {{ failedOverviewNames }}</span>
            <button
              type="button"
              class="overview-retry"
              :disabled="!overviewAllSettled"
              @click="retryOverviewFailures"
            >
              {{ overviewAllSettled ? 'Retry' : 'Waiting...' }}
            </button>
          </div>

          <div v-if="overviewShowFullError" class="overview-terminal-state" role="alert">
            <p>These categories could not be loaded.</p>
            <button type="button" class="overview-retry" @click="retryOverviewFailures">Try Again</button>
          </div>

          <div v-else-if="overviewShowEmpty" class="overview-terminal-state">
            <p v-if="majorAppliedSearch">No matches for "{{ majorAppliedSearch }}".</p>
            <p v-else>No listings are currently available in this category.</p>
          </div>

          <div v-else class="listing-overview-grid">
            <template v-for="section in overviewSections" :key="`${majorAppliedSearch || 'browse'}-${section.descriptor.classification.classificationKey}`">
              <div v-if="section.status === 'loading'" class="overview-placeholder category-batch-start">
                <span class="category-placeholder-label">{{ section.descriptor.label }}</span>
                <span class="overview-placeholder-line"></span>
                <span class="overview-placeholder-line overview-placeholder-line-short"></span>
              </div>

              <template v-else-if="section.status === 'loaded'">
                <ListingCard
                  v-for="(listing, index) in section.regularListings"
                  :key="`${section.descriptor.classification.classificationKey}-regular-${listing.orderNumber}-${index}`"
                  :ad="listing"
                  :category-label="section.descriptor.label"
                  :batch-start="index === 0"
                />
                <DisplayListingCard
                  v-for="(listing, index) in section.displayListings"
                  :key="`${section.descriptor.classification.classificationKey}-display-${listing.orderNumber}-${index}`"
                  :ad="listing"
                  :category-label="section.descriptor.label"
                  :batch-start="section.regularListings.length === 0 && index === 0"
                />
                <button
                  v-if="section.hasMore"
                  type="button"
                  class="overview-continuation"
                  @click="openOverviewContinuation(section)"
                >
                  {{ majorAppliedSearch ? 'View remaining matches' : 'View remaining listings' }} in {{ section.descriptor.label }}
                </button>
              </template>
            </template>
          </div>
        </div>

        <div v-else-if="selectedClassificationKey === null" class="select-prompt">
          <p>{{ selectClassificationText }}</p>
        </div>

        <div v-else-if="isLoadingClassification && !isSearching" class="classification-loading">
          <div class="loading-spinner"></div>
          <p>{{ loadingClassificationText }}</p>
        </div>

        <div
          v-else-if="(selectedClassificationListings && selectedClassificationListings.classificationKey === selectedClassificationKey) || leafSearchState"
          class="classification-listings"
        >
          <div class="classification-header">
            <h2 class="classification-title">
              {{ selectedClassificationInfo?.name || selectedClassificationListings?.name }}
            </h2>
            <span v-if="paginationInfo" class="classification-count">
              {{ showingListingCountText(paginationInfo.loadedCount, paginationInfo.totalCount) }}
            </span>
            <span v-else-if="selectedClassificationListings" class="classification-count">
              {{ listingCountText(selectedClassificationListings.totalAdCount) }}
            </span>
          </div>

          <div class="search-bar-container">
            <div class="search-input-wrapper">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="search-icon" aria-hidden="true">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
              <input
                v-model="searchInput"
                type="text"
                :placeholder="searchPlaceholder"
                class="search-input"
                @input="onSearchInput"
              />
              <button v-if="searchInput" type="button" class="search-clear-btn" title="Clear search" @click="clearSearch">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div v-if="isSearching" class="search-status">Searching...</div>
          </div>

          <div v-if="leafSearchError" class="overview-warning" role="alert">
            {{ leafSearchError }}
          </div>

          <div v-if="leafAppliedSearch && combinedListings.length === 0 && !isSearching" class="leaf-empty-state">
            <p>No results for "{{ leafAppliedSearch }}"</p>
          </div>

          <div v-if="combinedListings.length > 0" class="ads-merged-grid">
            <template v-for="(item, index) in combinedListings" :key="`${selectedClassificationKey}-${item.type}-${item.listing.orderNumber}-${index}`">
              <DisplayListingCard v-if="item.type === 'display'" :ad="item.listing" />
              <ListingCard v-else :ad="item.listing" />
            </template>
          </div>

          <div v-if="leafLoadMoreError" class="overview-warning" role="alert">
            <span>{{ leafLoadMoreError }}</span>
            <button
              type="button"
              class="overview-retry"
              :disabled="isLoadingMore || isSearching"
              @click="loadMore"
            >
              Retry
            </button>
          </div>

          <div v-if="paginationInfo?.hasMore" class="load-more-container">
            <button type="button" class="load-more-btn" :disabled="isLoadingMore || isSearching" @click="loadMore">
              <template v-if="isLoadingMore">
                <div class="loading-spinner" style="width: 20px; height: 20px; border-width: 2px;"></div>
                Loading...
              </template>
              <template v-else>
                Load More ({{ paginationInfo.totalCount - paginationInfo.loadedCount }} remaining)
              </template>
            </button>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<style scoped>
.home-view {
  min-height: calc(100vh - 64px);
  overflow: visible !important;
}

/* Header */
.page-header {
  background: var(--bg-secondary);
  border-bottom: 1px solid var(--border-color);
  padding: var(--spacing-6) 0;
  margin: calc(-1 * var(--spacing-6)) calc(-1 * var(--spacing-6)) var(--spacing-6);
  padding-left: var(--spacing-6);
  padding-right: var(--spacing-6);
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-4);
  flex-wrap: wrap;
}

.header-text {
  flex: 1;
  min-width: 200px;
}

.page-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
}

.page-subtitle {
  font-size: 0.9375rem;
  color: var(--text-secondary);
  margin: var(--spacing-1) 0 0;
}

.cta-link {
  text-decoration: none;
}

/* Loading State */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-16) var(--spacing-4);
  gap: var(--spacing-4);
}

.loading-spinner {
  width: 48px;
  height: 48px;
  border: 3px solid var(--border-color);
  border-top-color: var(--accent-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.loading-text {
  color: var(--text-secondary);
  font-size: 0.9375rem;
}

/* Error State */
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-16) var(--spacing-4);
  gap: var(--spacing-4);
}

.error-icon {
  width: 48px;
  height: 48px;
  border-radius: var(--radius-full);
  background: var(--error-subtle, rgba(239, 68, 68, 0.1));
  color: var(--error, #ef4444);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: 700;
}

.error-text {
  color: var(--error, #ef4444);
  font-size: 0.9375rem;
  text-align: center;
}

/* Empty State */
.empty-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-16) var(--spacing-4);
  gap: var(--spacing-4);
  text-align: center;
}

.empty-icon {
  color: var(--text-tertiary);
  margin-bottom: var(--spacing-2);
}

.empty-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.empty-text {
  color: var(--text-secondary);
  margin: 0 0 var(--spacing-4);
}

/* Main Layout */
.main-layout {
  display: grid;
  grid-template-columns: 280px minmax(0, 1fr);
  gap: 24px;
  align-items: start;
  overflow: visible !important;
}

/* Sidebar */
.sidebar {
  position: sticky;
  top: 88px; /* Header height + padding */
  align-self: start;
  max-height: calc(100vh - 112px);
  overflow-y: auto;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  transition: transform 0.1s ease-out;
  will-change: transform;
}

.sidebar-nav {
  padding: var(--spacing-4);
}

.sidebar-header {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--text-tertiary);
  padding: var(--spacing-2) var(--spacing-3);
  margin-bottom: var(--spacing-2);
}

.category-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.category-item {
  margin-bottom: var(--spacing-1);
}

.category-button {
  width: 100%;
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  padding: var(--spacing-2) var(--spacing-3);
  background: none;
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  text-align: left;
  transition: background 0.2s;
}

.category-button:hover {
  background: var(--bg-tertiary);
}

.category-control {
  display: flex;
  width: 100%;
  overflow: hidden;
  border-radius: var(--radius-md);
}

.category-select-button {
  display: flex;
  flex: 1;
  align-items: center;
  gap: var(--spacing-2);
  min-width: 0;
  padding: var(--spacing-2) var(--spacing-2) var(--spacing-2) var(--spacing-3);
  color: inherit;
  text-align: left;
  background: none;
  border: none;
  cursor: pointer;
  transition: background 0.2s;
}

.category-toggle-button {
  display: flex;
  flex: 0 0 34px;
  align-items: center;
  justify-content: center;
  padding: 0;
  color: inherit;
  background: none;
  border: none;
  cursor: pointer;
  transition: background 0.2s;
}

.category-select-button:hover,
.category-toggle-button:hover {
  background: var(--bg-tertiary);
}

.category-select-button.active {
  color: var(--accent-fill-text-color);
  background: var(--accent-primary);
}

.category-select-button.active .category-name,
.category-select-button.active .category-badge {
  color: var(--accent-fill-text-color);
}

.category-select-button.active .category-badge {
  background: color-mix(in srgb, var(--accent-fill-text-color) 18%, transparent);
}

.category-toggle-button.expanded .expand-icon {
  transform: rotate(180deg);
}

.category-name {
  flex: 1;
  font-size: 0.9375rem;
  font-weight: 500;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.category-badge {
  font-size: 0.75rem;
  color: var(--text-tertiary);
  background: var(--bg-primary);
  padding: 2px 8px;
  border-radius: var(--radius-full);
}

.expand-icon {
  color: var(--text-tertiary);
  transition: transform 0.2s;
  flex-shrink: 0;
}

.category-button.expanded .expand-icon {
  transform: rotate(180deg);
}

.subcategory-list {
  list-style: none;
  margin: 0;
  padding: 0 0 0 var(--spacing-4);
}

.subcategory-button {
  width: 100%;
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  padding: var(--spacing-2) var(--spacing-3);
  background: none;
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  text-align: left;
  transition: background 0.2s;
}

.subcategory-button:hover {
  background: var(--bg-tertiary);
}

.subcategory-button.active {
  background: var(--accent-primary);
  color: var(--accent-fill-text-color);
}

.subcategory-button.active .subcategory-name {
  color: var(--accent-fill-text-color);
}

.subcategory-button.active .subcategory-badge {
  color: var(--accent-fill-text-color);
}

.subcategory-name {
  flex: 1;
  font-size: 0.875rem;
  color: var(--text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.subcategory-badge {
  font-size: 0.6875rem;
  color: var(--text-tertiary);
}

/* Level 2 Subgroups (for 3-level hierarchy) */
.subgroup-item {
  margin-top: var(--spacing-1);
}

.subgroup-button {
  width: 100%;
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  padding: var(--spacing-2) var(--spacing-3);
  background: none;
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  text-align: left;
  transition: background 0.2s;
}

.subgroup-button:hover {
  background: var(--bg-tertiary);
}

.subgroup-control {
  display: flex;
  width: 100%;
  overflow: hidden;
  border-radius: var(--radius-md);
}

.subgroup-select-button {
  display: flex;
  flex: 1;
  align-items: center;
  gap: var(--spacing-2);
  min-width: 0;
  padding: var(--spacing-2) var(--spacing-2) var(--spacing-2) var(--spacing-3);
  color: inherit;
  text-align: left;
  background: none;
  border: none;
  cursor: pointer;
  transition: background 0.2s;
}

.subgroup-toggle-button {
  display: flex;
  flex: 0 0 30px;
  align-items: center;
  justify-content: center;
  padding: 0;
  color: inherit;
  background: none;
  border: none;
  cursor: pointer;
  transition: background 0.2s;
}

.subgroup-select-button:hover,
.subgroup-toggle-button:hover {
  background: var(--bg-tertiary);
}

.subgroup-select-button.active {
  color: var(--accent-fill-text-color);
  background: var(--accent-primary);
}

.subgroup-select-button.active .subgroup-name,
.subgroup-select-button.active .subgroup-badge {
  color: var(--accent-fill-text-color);
}

.subgroup-select-button.active .subgroup-badge {
  background: color-mix(in srgb, var(--accent-fill-text-color) 18%, transparent);
}

.subgroup-name {
  flex: 1;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.subgroup-badge {
  font-size: 0.6875rem;
  color: var(--text-tertiary);
  background: var(--bg-primary);
  padding: 2px 6px;
  border-radius: var(--radius-full);
}

.subgroup-button .expand-icon,
.subgroup-toggle-button .expand-icon {
  color: var(--text-tertiary);
  transition: transform 0.2s;
  flex-shrink: 0;
}

.subgroup-button.expanded .expand-icon,
.subgroup-toggle-button.expanded .expand-icon {
  transform: rotate(180deg);
}

/* Level 3 Classifications (deepest level) */
.level3-list {
  list-style: none;
  margin: 0;
  padding: 0 0 0 var(--spacing-4);
}

.level3-button {
  width: 100%;
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  padding: var(--spacing-2) var(--spacing-3);
  background: none;
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  text-align: left;
  transition: background 0.2s;
}

.level3-button:hover {
  background: var(--bg-tertiary);
}

.level3-button.active {
  background: var(--accent-primary);
  color: var(--accent-fill-text-color);
}

.level3-button.active .level3-name {
  color: var(--accent-fill-text-color);
}

.level3-button.active .level3-badge {
  color: var(--accent-fill-text-color);
}

.level3-name {
  flex: 1;
  font-size: 0.8125rem;
  color: var(--text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.level3-badge {
  font-size: 0.625rem;
  color: var(--text-tertiary);
}

/* Ads Content */
.ads-content {
  min-height: 400px;
  display: block !important;
}

.listing-panel {
  display: block;
  min-height: 400px;
  padding: 20px;
  background: #fafafa;
  border-radius: 8px;
}

.classification-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 100px 20px;
  color: #666;
  font-size: 0.9375rem;
}

.classification-listings,
.major-overview {
  min-width: 0;
}

.major-overview-header {
  flex-wrap: wrap;
  margin-bottom: 16px;
}

.overview-search-form {
  display: flex;
  gap: var(--spacing-2);
  align-items: center;
  margin-bottom: 16px;
}

.overview-search-form .search-input-wrapper {
  flex: 1;
}

.overview-search-submit,
.overview-retry,
.overview-continuation {
  color: var(--accent-fill-text-color);
  font-weight: 600;
  background: var(--accent-primary);
  border: 1px solid var(--accent-primary);
  border-radius: var(--radius-md);
  cursor: pointer;
}

.overview-search-submit {
  align-self: stretch;
  padding: 0 18px;
}

.overview-search-submit:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.overview-retry:disabled {
  cursor: wait;
  opacity: 0.65;
}

.overview-warning {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-3);
  padding: var(--spacing-3);
  margin-bottom: 16px;
  color: #713f12;
  background: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: var(--radius-md);
}

.overview-retry {
  flex-shrink: 0;
  padding: 6px 12px;
}

.overview-terminal-state,
.leaf-empty-state {
  padding: 60px 20px;
  color: #666;
  text-align: center;
}

.listing-overview-grid {
  column-count: 3;
  column-gap: 20px;
}

.listing-overview-grid > :deep(*) {
  display: inline-block;
  width: 100%;
  margin-bottom: 20px;
  break-inside: avoid;
}

.listing-overview-grid > :deep(.category-batch-start) {
  margin-top: 10px;
}

.overview-placeholder {
  min-height: 128px;
  overflow: hidden;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
}

.category-placeholder-label {
  display: block;
  width: fit-content;
  max-width: calc(100% - var(--spacing-4));
  padding: 0.3rem 0.65rem;
  overflow: hidden;
  color: var(--accent-fill-text-color);
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
  background: var(--accent-primary);
  border-bottom-right-radius: var(--radius-sm);
}

.overview-placeholder-line {
  display: block;
  width: calc(100% - 32px);
  height: 11px;
  margin: 24px 16px 0;
  background: var(--bg-tertiary);
  border-radius: var(--radius-full);
  animation: placeholder-pulse 1.4s ease-in-out infinite;
}

.overview-placeholder-line-short {
  width: 58%;
  margin-top: 12px;
}

.overview-continuation {
  padding: 16px;
  color: var(--accent-primary);
  text-align: left;
  background: color-mix(in srgb, var(--accent-primary) 8%, var(--bg-primary));
  border-style: dashed;
}

.overview-continuation:hover,
.overview-retry:hover,
.overview-search-submit:not(:disabled):hover {
  filter: brightness(0.94);
}

@keyframes placeholder-pulse {
  0%, 100% { opacity: 0.55; }
  50% { opacity: 1; }
}

.select-prompt {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  color: var(--text-tertiary);
  font-size: 1rem;
  text-align: center;
}

.classification-ads {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-6);
}

.classification-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-3);
  padding-bottom: var(--spacing-4);
  border-bottom: 2px solid var(--accent-line-color);
}

.classification-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
}

.classification-count {
  font-size: 0.875rem;
  color: var(--text-tertiary);
  background: var(--bg-tertiary);
  padding: 4px 12px;
  border-radius: var(--radius-full);
}

.section-label {
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--text-tertiary);
  margin: 0 0 var(--spacing-3) 0;
}

/* Unified masonry grid for display + regular ads */
.ads-merged-grid {
  column-count: 3;
  column-gap: 20px;
}

.ads-merged-grid > * {
  break-inside: avoid;
  margin-bottom: 20px;
  display: inline-block;
  width: 100%;
}

@media (max-width: 1200px) {
  .ads-merged-grid,
  .listing-overview-grid {
    column-count: 2;
  }
}

@media (max-width: 900px) {
  .ads-merged-grid,
  .listing-overview-grid {
    column-count: 1;
  }
}

/* Responsive */
@media (max-width: 1024px) {
  .main-layout {
    grid-template-columns: 240px 1fr;
  }
}

@media (max-width: 768px) {
  .page-header {
    margin: calc(-1 * var(--spacing-4)) calc(-1 * var(--spacing-4)) var(--spacing-4);
    padding: var(--spacing-4);
  }
  
  .header-content {
    flex-direction: column;
    align-items: stretch;
    text-align: center;
  }
  
  .main-layout {
    grid-template-columns: 1fr;
  }
  
  .sidebar {
    position: static;
    max-height: none;
    margin-bottom: var(--spacing-4);
  }
  
  .page-title {
    font-size: 1.5rem;
  }

  .listing-panel {
    padding: var(--spacing-4);
  }

  .overview-search-form {
    align-items: stretch;
    flex-direction: column;
  }

  .overview-search-submit {
    min-height: 42px;
  }

}

/* Search Bar */
.search-bar-container {
  margin-bottom: 16px;
}

.search-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 12px;
  color: #999;
  pointer-events: none;
}

.search-input {
  width: 100%;
  padding: 10px 40px 10px 38px;
  font-size: 0.9375rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  outline: none;
  background: #fff;
  color: #1a1a1a;
  transition: border-color 0.2s;
}

.search-input:focus {
  border-color: var(--accent-primary);
  box-shadow: 0 0 0 3px var(--accent-subtle);
}

.search-input::placeholder {
  color: #aaa;
}

.search-clear-btn {
  position: absolute;
  right: 8px;
  background: none;
  border: none;
  cursor: pointer;
  color: #999;
  padding: 4px;
  display: flex;
  align-items: center;
  border-radius: 50%;
}

.search-clear-btn:hover {
  color: #333;
  background: #eee;
}

.search-status {
  font-size: 0.8125rem;
  color: #999;
  margin-top: 6px;
  padding-left: 4px;
}

/* Load More */
.load-more-container {
  display: flex;
  justify-content: center;
  padding: 24px 0 8px;
}

.load-more-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 24px;
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--accent-primary);
  background: #fff;
  border: 2px solid var(--accent-primary);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.load-more-btn:hover:not(:disabled) {
  background: var(--button-background);
  color: var(--button-text-color);
}

.load-more-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

</style>
